<template>
    <v-col v-if="loading" cols="12">
        <v-skeleton-loader class="pt-4" type="paragraph" />
    </v-col>
    <v-col v-else cols="12" class="mt-5 text-caption text-md-body-2">
        <v-card
            v-for="({flightSegments, duration: totalDuration}, index) in itineraries"
            :key="index"
            variant="outlined"
            class="mb-2"
        >
            <v-row no-gutters class="pa-2 pa-md-4 gr-4">
                <v-col cols="12" md="12" lg="10">
                    <v-row no-gutters>
                        <v-col cols="4">
                            <div class="text-subtitle-2 font-weight-bold">
                                <span v-if="firstSegment(flightSegments).brandedFare">
                                    {{ firstSegment(flightSegments).brandedFare.name }}
                                </span>
                                <span
                                    v-if="
                                        firstSegment(flightSegments).brandedFare &&
                                        firstSegment(flightSegments).serviceClass
                                    "
                                >
                                    {{ ` / ` }}
                                </span>
                                <span v-if="firstSegment(flightSegments).serviceClass">
                                    {{ firstSegment(flightSegments).serviceClass }}
                                </span>
                            </div>
                            <div v-if="firstSegment(flightSegments).departureDate" class="font-weight-bold">
                                {{ $filters.hoursFromDate(firstSegment(flightSegments).departureDate) }}
                            </div>
                            <div class="text-no-wrap">
                                {{ $filters.dateFormat(firstSegment(flightSegments).departureDate) }}
                            </div>
                            <v-tooltip location="top">
                                <template v-slot:activator="{props}">
                                    <div
                                        v-if="departureAirport(flightSegments)"
                                        class="text-medium-emphasis"
                                        :class="{'cursor-pointer': firstSegment(flightSegments).departureTerminal}"
                                        v-on="firstSegment(flightSegments).departureTerminal ? props : null"
                                    >
                                        {{ departureAirport(flightSegments).name }}
                                        {{
                                            departureAirport(flightSegments).iataCode &&
                                            `(${departureAirport(flightSegments).iataCode})`
                                        }}
                                    </div>
                                </template>
                                <span>
                                    {{ firstSegment(flightSegments).departureTerminal }}
                                </span>
                            </v-tooltip>
                        </v-col>
                        <v-col cols="4" class="px-3">
                            <v-row no-gutters class="justify-space-between text-medium-emphasis">
                                <template v-if="totalDuration">
                                    <v-icon class="d-none d-md-block" size="small">mdi-airplane-takeoff</v-icon>
                                    <div class="d-block d-md-none" />
                                    <div v-if="totalDuration">
                                        {{ $filters.durationFormat(totalDuration) }}
                                    </div>
                                    <div class="d-block d-md-none" />
                                    <v-icon class="d-none d-md-block" size="small">mdi-airplane-landing</v-icon>
                                </template>
                                <template v-else>
                                    <v-icon size="small">mdi-airplane-takeoff</v-icon>
                                    <v-icon size="small">mdi-airplane-landing</v-icon>
                                </template>
                            </v-row>
                            <v-row no-gutters>
                                <template v-if="flightSegments.length > 1">
                                    <div class="line-container my-2">
                                        <div />
                                        <div
                                            v-for="(segment, segmentIndex) in pathPoints(flightSegments).length - 2"
                                            :key="segmentIndex"
                                            class="line-container__dot"
                                        />
                                        <div />
                                    </div>
                                </template>
                                <v-divider v-else class="my-2" />
                            </v-row>
                            <v-row no-gutters class="justify-space-between">
                                <template v-if="flightSegments.length > 1">
                                    <div class="d-block d-md-none" />
                                    <div
                                        v-for="(segment, segmentIndex) in pathPoints(flightSegments)"
                                        :key="segmentIndex"
                                        :class="{
                                            'd-none d-md-block':
                                                segmentIndex === 0 ||
                                                segmentIndex === pathPoints(flightSegments).length - 1,
                                        }"
                                    >
                                        {{ segment }}
                                    </div>
                                    <div class="d-block d-md-none" />
                                </template>
                                <template v-else>
                                    <div>{{ departureAirport(flightSegments).iataCode }}</div>
                                    <div>{{ arrivalAirport(flightSegments).iataCode }}</div>
                                </template>
                            </v-row>
                        </v-col>
                        <v-col cols="4" class="text-end">
                            <div v-if="lastSegment(flightSegments).arrivalDate" class="font-weight-bold">
                                {{ $filters.hoursFromDate(lastSegment(flightSegments).arrivalDate) }}
                            </div>
                            <div class="text-no-wrap">
                                {{ $filters.dateFormat(lastSegment(flightSegments).arrivalDate) }}
                            </div>
                            <v-tooltip location="top">
                                <template v-slot:activator="{props}">
                                    <div
                                        v-if="arrivalAirport(flightSegments)"
                                        class="text-medium-emphasis"
                                        :class="{'cursor-pointer': lastSegment(flightSegments).departureTerminal}"
                                        v-on="lastSegment(flightSegments).arrivalTerminal ? props : null"
                                    >
                                        {{ arrivalAirport(flightSegments).name }}
                                        {{
                                            arrivalAirport(flightSegments).iataCode &&
                                            `(${arrivalAirport(flightSegments).iataCode})`
                                        }}
                                    </div>
                                </template>
                                <span>
                                    {{ lastSegment(flightSegments).arrivalTerminal }}
                                </span>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" md="12" lg="2" class="align-center align-content-center">
                    <div class="text-center cursor-pointer" @click="showModal(flightSegments, totalDuration)">
                        {{ $t('details') }}
                    </div>
                </v-col>
            </v-row>
        </v-card>
        <ModalWrapper v-model="modal" :fullscreen="isMobile" max-width="800" hide-actions>
            <template v-slot:title>
                {{ formattedLocation(selectedSegments) }}
            </template>
            <template v-slot:content>
                <div
                    v-for="(
                        {
                            marketingAirline,
                            flightNumber,
                            serviceClass,
                            bookingClass,
                            arrivalAirportCode,
                            stopLocations,
                            flightClass,
                            brandedFare,
                            mealIncluded,
                            aircraftName,
                            duration,
                            baggageInfo,
                            departureDate,
                            arrivalDate,
                            departureAirportCode,
                        },
                        flightSegmentIndex
                    ) in selectedSegments"
                    :key="flightSegmentIndex"
                    class="mb-6 text-body-2"
                >
                    <v-row no-gutters class="mb-4">
                        <v-col cols="12" md="4" class="mb-3 mb-md-0 pr-md-10 d-flex align-center">
                            <v-img
                                v-if="marketingAirline"
                                :src="logo(marketingAirline)"
                                :max-height="80"
                                class="flight-img"
                            />
                            <span v-else class="text-caption py-2">
                                {{ $t('no_airline_logo') }}
                            </span>
                        </v-col>
                        <v-col
                            cols="12"
                            md="8"
                            :class="{
                                container:
                                    (departureAirportCode || departureDate) && (arrivalAirportCode || arrivalDate),
                            }"
                        >
                            <div class="line" />
                            <div class="d-flex flex-column justify-space-between full-width">
                                <div v-if="departureDate || departureAirportCode" class="flight-info-wrapper pb-4">
                                    <div>{{ departureDate && $filters.hoursFromDate(departureDate) }}</div>
                                    <div>{{ departureAirportCode && airport(departureAirportCode).cityName }}</div>
                                    <div class="text-medium-emphasis">
                                        {{ departureDate && $filters.dateFormat(departureDate) }}
                                    </div>
                                    <div class="text-medium-emphasis">
                                        {{
                                            [airport(departureAirportCode).name, departureAirportCode]
                                                .filter(val => val)
                                                .join(', ')
                                        }}
                                    </div>
                                </div>
                                <div v-if="arrivalDate || arrivalAirportCode" class="flight-info-wrapper">
                                    <div>{{ arrivalDate && $filters.hoursFromDate(arrivalDate) }}</div>
                                    <div>{{ arrivalAirportCode && airport(arrivalAirportCode).cityName }}</div>
                                    <div class="text-medium-emphasis">
                                        {{ arrivalDate && $filters.dateFormat(arrivalDate) }}
                                    </div>
                                    <div class="text-medium-emphasis">
                                        {{
                                            [airport(arrivalAirportCode).name, arrivalAirportCode]
                                                .filter(val => val)
                                                .join(', ')
                                        }}
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mb-6">
                        <v-col cols="12" md="4" class="pr-3">
                            <div v-if="aircraftName">{{ aircraftName }}</div>
                        </v-col>
                        <v-col cols="12" md="8">
                            <div class="flight-info-wrapper">
                                <template v-if="serviceClass || flightClass">
                                    <div class="text-medium-emphasis">{{ $t('flights.class') }}</div>
                                    <span>
                                        <span v-if="serviceClass">
                                            {{ getServiceClassLabel(serviceClass) }}
                                        </span>
                                        <span v-else-if="flightClass">
                                            {{ flightClass }}
                                        </span>
                                        <template v-if="bookingClass">&nbsp;({{ bookingClass }})</template>
                                    </span>
                                </template>

                                <template v-if="brandedFare && brandedFare.name">
                                    <div class="text-medium-emphasis">{{ $t('tariff') }}</div>
                                    <div class="text-primary">{{ brandedFare.name }}</div>
                                </template>

                                <template v-if="mealIncluded !== undefined">
                                    <div class="text-medium-emphasis">{{ $t('meal_included') }}</div>
                                    <v-icon v-if="mealIncluded" size="small" start color="success">
                                        mdi-check-circle
                                    </v-icon>
                                    <v-icon v-else size="small" start color="error"> mdi-close-circle </v-icon>
                                </template>

                                <template v-if="flightNumber || marketingAirline">
                                    <div class="text-medium-emphasis">{{ $t('flight_number') }}</div>
                                    <div>{{ marketingAirline }} {{ flightNumber }}</div>
                                </template>
                                <template v-if="baggageInfo?.length">
                                    <div class="text-medium-emphasis">{{ $t('flights.baggage') }}</div>
                                    <div>
                                        <div v-for="({baggage}, baggageInd) in baggageInfo" :key="baggageInd">
                                            {{
                                                baggage.unitQuantity != 0
                                                    ? `${$t('flights.up_to')} ${baggage.unitQuantity} ${$t(`flights.baggageType.${baggage.unitCode}`, +baggage.unitQuantity)} `
                                                    : $t('flights.no_baggage')
                                            }}
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </v-col>
                    </v-row>
                    <v-card variant="tonal" class="py-1 px-2 d-flex gc-4 mt-3">
                        <div v-if="duration">{{ $filters.durationFormat(duration) }}</div>
                        <div v-if="!stopLocations || !stopLocations.length" class="text-success">
                            {{ $t('flights.nonstop') }}
                        </div>
                        <div v-else class="text-info">{{ stopLocations.length }} {{ $t('stops') }}</div>
                        <div
                            v-if="selectedSegments.length > 1 && flightSegmentIndex !== selectedSegments.length - 1"
                            class="text-truncate"
                        >
                            {{ airport(arrivalAirportCode).cityName }} ({{ airport(arrivalAirportCode).iataCode }})
                        </div>
                        <div v-else>
                            {{ $t('flights.flight_transfer', selectedSegments.length - 1) }}
                        </div>
                    </v-card>
                </div>
                <div v-if="selectedTotalDuration" class="text-medium-emphasis text-body-2">
                    {{ $t('flights.total_trip_time') }}:
                    {{ $filters.durationFormat(selectedTotalDuration) }}
                </div>
            </template>
        </ModalWrapper>
    </v-col>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {fullUrl} from '~/utils/host-adapter'

    @Component({
        components: {},
    })
    export class FlightServiceAdditionalInfo extends Vue {
        @Prop() service

        airports = []
        loading = false
        modal = false
        selectedSegments = null
        selectedTotalDuration = null

        showModal(flightSegments, totalDuration) {
            this.selectedSegments = flightSegments
            this.selectedTotalDuration = totalDuration
            this.modal = true
        }

        async mounted() {
            this.loading = true
            const airportCodes = []
            this.itineraries.forEach(({flightSegments}) => {
                flightSegments.forEach(({arrivalAirportCode, departureAirportCode}) => {
                    if (!airportCodes.includes(arrivalAirportCode)) airportCodes.push(arrivalAirportCode)
                    if (!airportCodes.includes(departureAirportCode)) airportCodes.push(departureAirportCode)
                })
            })
            try {
                const responses = await Promise.all(airportCodes.map(iataCode => this.$api.airports.get({iataCode})))
                this.airports = responses.flat()
            } catch (e) {
                this.airports = []
            }
            this.loading = false
        }

        firstSegment(flightSegments) {
            return flightSegments[0]
        }

        lastSegment(flightSegments) {
            return flightSegments[flightSegments.length - 1]
        }

        departureAirport(flightSegments) {
            return this.airport(this.firstSegment(flightSegments).departureAirportCode)
        }

        arrivalAirport(flightSegments) {
            return this.airport(this.lastSegment(flightSegments).arrivalAirportCode)
        }

        airport(iataCode) {
            return this.airports.find(airport => airport.iataCode === iataCode) || {iataCode}
        }

        getServiceClassLabel(serviceClass) {
            return this.$t(
                `flights.classes.${serviceClass
                    .split(/\.?(?=[A-Z])/)
                    .join('_')
                    .toUpperCase()}`
            )
        }

        get itineraries() {
            if (this.service.serviceType === 'EXTRASERVICE') {
                return this.service.serviceDetails[0]?.segments.map(segment => ({
                    flightSegments: [segment],
                }))
            }

            return this.service.serviceDetails[0].itineraries
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        formattedLocation(flightSegments) {
            const parts = [
                this.departureAirport(flightSegments).name,
                this.departureAirport(flightSegments).cityName,
                this.arrivalAirport(flightSegments).name,
                this.arrivalAirport(flightSegments).cityName,
            ].filter(val => val)
            return parts.join(', ')
        }

        pathPoints(flightSegments) {
            const points = []
            flightSegments.forEach((flight, index) => {
                if (index === 0) {
                    points.push(flight.departureAirportCode)
                }
                if (index > 0 && flight.departureAirportCode !== flightSegments[index - 1].arrivalAirportCode) {
                    points.push(flight.departureAirportCode)
                }
                points.push(flight.arrivalAirportCode)
            })
            return points
        }

        logo(airline) {
            return fullUrl(`/stat/images/airlines/${airline}.gif`)
        }
    }

    export default FlightServiceAdditionalInfo
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    .flight-img :deep(.v-img__img) {
        object-position: left center;
    }
    .line-container {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }
    .line-container::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 1px;
        background-color: rgba(var(--v-theme-on-background), var(--v-border-opacity));
    }
    .line-container__dot {
        width: 5px;
        height: 5px;
        background-color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
        border-radius: 50%;
        z-index: 1;
    }

    .container {
        position: relative;
        display: flex;
    }
    .container::before,
    .container::after {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        background-color: white;
        outline: 1px solid rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity));
        border-radius: 50%;
    }
    .container::before {
        left: -24px;
        top: 10px;
    }
    .container::after {
        bottom: 10px;
        left: -24px;
    }
    .line {
        position: absolute;
        width: 1px;
        top: 18px;
        bottom: 18px;
        left: -20px;
        background-color: rgba(var(--v-theme-on-background), var(--v-border-opacity));
    }

    .flight-info-wrapper {
        display: grid;
        grid-template-columns: 30% 70%;
        @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
            grid-template-columns: 40% 60%;
        }
    }

    table {
        table-layout: fixed;

        @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
            width: 100%;
        }
    }
</style>
